import React, { useEffect, useRef, useState, useCallback } from 'react';
import * as atlas from 'azure-maps-control';
import 'azure-maps-control/dist/atlas.min.css';
import { Pin } from '@gps-hunt/shared';

interface ParticipantLocation {
  userId: string;
  displayName: string;
  latitude: number;
  longitude: number;
  role: 'hunter' | 'hider';
}

interface SpectatorMapProps {
  subscriptionKey: string;
  center?: [number, number];
  zoom?: number;
  pins?: Pin[];
  activePinIndex?: number;
  completedPinIds?: string[];
  participants?: ParticipantLocation[];
  userLocation?: { latitude: number; longitude: number; accuracy: number };
  className?: string;
}

/**
 * Spectator map for hiders during active hunt
 * Shows all participants and pins with status indicators
 */
export const SpectatorMap: React.FC<SpectatorMapProps> = ({
  subscriptionKey,
  center, // No default - will center on first valid location/pins
  zoom = 15,
  pins = [],
  activePinIndex = 0,
  completedPinIds = [],
  participants = [],
  userLocation,
  className = '',
}) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<atlas.Map | null>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const participantMarkersRef = useRef<atlas.HtmlMarker[]>([]);
  const pinMarkersRef = useRef<atlas.HtmlMarker[]>([]);
  const userMarkerRef = useRef<atlas.HtmlMarker | null>(null);
  const userHasInteractedRef = useRef(false);
  const hasInitializedCenterRef = useRef(false);

  // Initialize map (intentionally only depends on subscriptionKey to avoid reinitializing)
  useEffect(() => {
    if (!mapRef.current || mapInstanceRef.current) return;

    // Determine initial center: use provided center, or pins, or userLocation, or world view
    let initialCenter: [number, number] = [0, 0];
    let initialZoom = 2;
    
    if (center) {
      initialCenter = center;
      initialZoom = zoom;
      hasInitializedCenterRef.current = true;
    } else if (pins.length > 0) {
      initialCenter = [pins[0].longitude, pins[0].latitude];
      initialZoom = zoom;
      hasInitializedCenterRef.current = true;
    } else if (userLocation) {
      initialCenter = [userLocation.longitude, userLocation.latitude];
      initialZoom = zoom;
      hasInitializedCenterRef.current = true;
    }

    const map = new atlas.Map(mapRef.current, {
      center: initialCenter,
      zoom: initialZoom,
      style: 'night',
      language: 'en-US',
      authOptions: {
        authType: atlas.AuthenticationType.subscriptionKey,
        subscriptionKey: subscriptionKey,
      },
    });

    map.events.add('ready', () => {
      setIsLoaded(true);
      
      // Track user interaction to prevent auto-recentering
      map.events.add('dragstart', () => {
        userHasInteractedRef.current = true;
      });
      map.events.add('wheel', () => {
        userHasInteractedRef.current = true;
      });
    });

    mapInstanceRef.current = map;

    return () => {
      map.dispose();
      mapInstanceRef.current = null;
      hasInitializedCenterRef.current = false;
      userHasInteractedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subscriptionKey]);

  // Center map only once on first valid data (not after user interaction)
  useEffect(() => {
    if (!mapInstanceRef.current || !isLoaded || userHasInteractedRef.current || hasInitializedCenterRef.current) return;
    
    // Try to center on: provided center, first pin, or user location
    if (center) {
      mapInstanceRef.current.setCamera({
        center: center,
        zoom: zoom,
      });
      hasInitializedCenterRef.current = true;
    } else if (pins.length > 0) {
      mapInstanceRef.current.setCamera({
        center: [pins[0].longitude, pins[0].latitude],
        zoom: zoom,
      });
      hasInitializedCenterRef.current = true;
    } else if (userLocation) {
      mapInstanceRef.current.setCamera({
        center: [userLocation.longitude, userLocation.latitude],
        zoom: zoom,
      });
      hasInitializedCenterRef.current = true;
    }
  }, [center, pins, userLocation, zoom, isLoaded]);

  // Update pin markers
  useEffect(() => {
    if (!mapInstanceRef.current || !isLoaded) return;

    // Clear existing pin markers
    pinMarkersRef.current.forEach(marker => {
      mapInstanceRef.current?.markers.remove(marker);
    });
    pinMarkersRef.current = [];

    // Add new pin markers
    pins.forEach((pin, index) => {
      const isActive = index === activePinIndex;
      const isCompleted = completedPinIds.includes(pin.id);
      
      const color = isCompleted ? '#666666' : isActive ? '#00D4FF' : '#FF6B6B';
      const animation = isActive && !isCompleted ? 'animation: pinPulse 1.5s ease-in-out infinite;' : '';
      
      const markerHtml = `
        <div style="position: relative; text-align: center;">
          <div style="
            width: 32px;
            height: 32px;
            border-radius: 50% 50% 50% 0;
            background: ${color};
            transform: rotate(-45deg);
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
            ${animation}
          ">
            <span style="
              transform: rotate(45deg);
              color: white;
              font-weight: bold;
              font-size: 14px;
            ">${index + 1}</span>
          </div>
          ${isActive ? '<style>@keyframes pinPulse { 0%, 100% { transform: rotate(-45deg) scale(1); opacity: 1; } 50% { transform: rotate(-45deg) scale(1.2); opacity: 0.8; } }</style>' : ''}
        </div>
      `;

      const marker = new atlas.HtmlMarker({
        position: [pin.longitude, pin.latitude],
        htmlContent: markerHtml,
        anchor: 'bottom',
      });

      mapInstanceRef.current?.markers.add(marker);
      pinMarkersRef.current.push(marker);
    });
  }, [pins, activePinIndex, completedPinIds, isLoaded]);

  // Update participant markers
  useEffect(() => {
    if (!mapInstanceRef.current || !isLoaded) return;

    // Clear existing participant markers
    participantMarkersRef.current.forEach(marker => {
      mapInstanceRef.current?.markers.remove(marker);
    });
    participantMarkersRef.current = [];

    // Add participant markers
    participants.forEach(participant => {
      const color = participant.role === 'hunter' ? '#EF4444' : '#22C55E';
      
      const markerHtml = `
        <div style="position: relative; text-align: center;">
          <div style="
            font-size: 10px;
            color: white;
            background: rgba(0, 0, 0, 0.7);
            padding: 2px 6px;
            border-radius: 4px;
            white-space: nowrap;
            margin-bottom: 4px;
          ">${participant.displayName}</div>
          <div style="
            width: 16px;
            height: 16px;
            border-radius: 50%;
            background: ${color};
            border: 2px solid white;
            box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
            margin: 0 auto;
          "></div>
        </div>
      `;

      const marker = new atlas.HtmlMarker({
        position: [participant.longitude, participant.latitude],
        htmlContent: markerHtml,
        anchor: 'bottom',
      });

      mapInstanceRef.current?.markers.add(marker);
      participantMarkersRef.current.push(marker);
    });
  }, [participants, isLoaded]);

  // Update user location marker
  useEffect(() => {
    if (!mapInstanceRef.current || !isLoaded || !userLocation) return;

    if (userMarkerRef.current) {
      mapInstanceRef.current.markers.remove(userMarkerRef.current);
    }

    const markerHtml = `
      <div style="position: relative;">
        <div style="
          width: ${Math.min(userLocation.accuracy * 2, 100)}px;
          height: ${Math.min(userLocation.accuracy * 2, 100)}px;
          border-radius: 50%;
          background: rgba(34, 197, 94, 0.2);
          border: 2px solid rgba(34, 197, 94, 0.5);
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        "></div>
        <div style="
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #22C55E;
          border: 3px solid white;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);
          position: relative;
          z-index: 1;
        "></div>
      </div>
    `;

    const marker = new atlas.HtmlMarker({
      position: [userLocation.longitude, userLocation.latitude],
      htmlContent: markerHtml,
    });

    mapInstanceRef.current.markers.add(marker);
    userMarkerRef.current = marker;
  }, [userLocation, isLoaded]);

  const centerOnUser = useCallback(() => {
    if (mapInstanceRef.current && userLocation) {
      mapInstanceRef.current.setCamera({
        center: [userLocation.longitude, userLocation.latitude],
        zoom: 17,
      });
    }
  }, [userLocation]);

  return (
    <div className={`relative ${className}`}>
      <div
        ref={mapRef}
        className="w-full h-full overflow-hidden"
        style={{ minHeight: '300px' }}
      />
      
      {userLocation && (
        <button
          onClick={centerOnUser}
          className="absolute bottom-4 right-4 w-12 h-12 bg-secondary rounded-full
                     flex items-center justify-center shadow-lg
                     hover:bg-secondary/80 transition-colors"
          aria-label="Center on my location"
        >
          <svg
            className="w-6 h-6 text-green-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <circle cx="12" cy="12" r="3" strokeWidth="2" />
            <path strokeWidth="2" d="M12 2v4m0 12v4m10-10h-4M6 12H2" />
          </svg>
        </button>
      )}

      {!isLoaded && (
        <div className="absolute inset-0 bg-background/80 flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
        </div>
      )}
    </div>
  );
};

export default SpectatorMap;
